import "core-js/stable";
import "regenerator-runtime/runtime";

import React from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { hasRole, useActiveRoles, hasAtLeastOneRole } from "./utils/permissionsUtils.tsx";
import { routesConfig } from "./routesConfig";
import { RoleEnum } from "./types/RoleEnum.ts";
import Settings from "./settings";
import { AccessDenied } from "./components/AccessDenied.tsx";
import { RouteGuard } from "./guards/RouteGuard.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  },
});

const BrUsers = (props) => {
  const { roles: activeRoles, isLoading } = useActiveRoles();
  const {
    showSettings,
    match: { path },
    stripes,
  } = props;

  if (showSettings) {
    return <Settings {...props} />;
  }

  // Redirect users who haven't selected an organization yet
  // When no organization is selected, activeRoles is an empty array
  // But don't redirect if already on the select-organization page
  const needsOrgSelection =
    !isLoading && Array.isArray(activeRoles) && activeRoles.length === 0;
  const isOnSelectOrgPage = window.location.pathname.includes("/select-organization");

  if (needsOrgSelection && !isOnSelectOrgPage) {
    return <Redirect to="/brusers/my-profile/select-organization" />;
  }

  const connectedRoutes = Object.keys(routesConfig).reduce((acc, key) => {
    acc[key] = stripes.connect(routesConfig[key].component);
    return acc;
  }, {});

  return (
    <QueryClientProvider client={queryClient}>
      <Switch>
        {Object.keys(routesConfig).map((key) => {
          const {
            path: routePath,
            roles,
          } = routesConfig[key];
          const Component = connectedRoutes[key];

          return (
            <Route
              key={key}
              path={`${path}${routePath}`}
              render={(routeProps) => (
                <RouteGuard
                  allowedRoles={roles || []}
                  fallbackComponent={<AccessDenied showNavigation />}
                >
                  <Component {...props} {...routeProps} />
                </RouteGuard>
              )}
            />
          );
        })}
        <Route
          render={() => {
            if (isLoading) {
              return null;
            }

            const isSuperUser = activeRoles && hasRole(activeRoles, RoleEnum.SUPERUSER);

            if (isSuperUser) {
              const firstRoute = Object.values(routesConfig)[0];
              return <Redirect to={`${path}${firstRoute.path}`} />;
            }

            const accessibleRoute = Object.values(routesConfig).find(route => {
              if (!route.roles) return true;
              return activeRoles && hasAtLeastOneRole(activeRoles, route.roles);
            });

            if (accessibleRoute) {
              return <Redirect to={`${path}${accessibleRoute.path}`} />;
            }

            return <AccessDenied showNavigation />;
          }}
        />
      </Switch>
    </QueryClientProvider>
  );
};

BrUsers.propTypes = {
  match: PropTypes.object.isRequired,
  showSettings: PropTypes.bool,
  stripes: PropTypes.shape({
    connect: PropTypes.func,
  }),
};

export default BrUsers;
